import React, { useEffect } from 'react';

import config from 'config';
import { BsArrowRight } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import ReviewsHeader from '~/layout/components/ReviewsHeader';
import SizeFabricGuideModal from '~/shared/components/SizeFabricGuideModal';
import { productDetailPath } from '~/shared/constants/product';
import { ProductTag } from '~/shared/constants/products';
import getImageUrl from '~/shared/utils/getImageUrl';
import { isIneligibleFitGuideProduct } from '~/shared/utils/isIneligibleFitGuideProduct';
import { isMysteryBox } from '~/shared/utils/isMysteryBox';
import logger from '~/shared/utils/logger';
import SizeFabricGuideFableticsModal from '~/staticPages/components/SizeFabricGuideFableticsModal';
import { useMembership } from '~/techstyle-shared/react-accounts';
import {
  mobile,
  ProductImage as BaseProductImage,
  ProductName as BaseProductName,
  useProductDetailContext,
  useProductDetailSelectionState,
  useProductContext,
  useReviewsDrawer,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import { trackProductClicked } from '~/techstyle-shared/react-products';

import { useProductQueryAndPreload } from '../../utils/useProductQueryAndPreload';
import ProductDetailColorSelector from '../ProductDetailColorSelector';
import { useProductDetailEmbroideryContext } from '../ProductDetailCTAs/ProductDetailEmbroideryContext';
import ProductDetailEmbroiderySelector from '../ProductDetailEmbroiderySelector.js';
import ProductDetailOtherSelectors from '../ProductDetailOtherSelectors';
import ProductDetailSizeSelector from '../ProductDetailSizeSelector';
import ProductDetailsSection from '../ProductDetailsSection';
import ScrubsSizeGuideModal from '../ScrubsSizeGuideModal';

const debug = logger.extend('ProductDetails');

const outfitImageBreakpoints = [{ minWidth: 0, pictureWidth: 100 }];

const OutfitWrapper = styled.div`
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.outfitItemWrapper},
    0 1px 2px 0 rgba(0, 0, 0, 0.08);
  background: ${({ theme }) => theme.colors.palette.white};
  margin-bottom: 24px;
  ${mobile`
    margin-bottom: 8px;
  `}
`;

const OutfitWrapperContent = styled.div`
  padding: 16px;
  ${mobile`
    padding: 0;
  `}
  cursor: pointer;
`;

const OutfitDetailsWrapper = styled.div`
  ${mobile`
    padding: 16px;
  `}
`;

const OutfitWrapperActions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.outfitItemActions};
  padding: 16px;
  cursor: pointer;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OutfitItemImageWrapper = styled.a`
  align-items: center;
  display: flex;
  margin-bottom: 16px;

  ${mobile`
    padding: 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.outfitItemImage};
    margin-bottom: 0;
  `}
`;

const OutfitProductImage = styled(BaseProductImage)`
  flex-basis: 64px;
  flex-shrink: 0;
  margin-right: 12px;
  max-height: 96px;
`;

const OutfitProductName = styled(BaseProductName)`
  color: ${({ theme }) => theme.colors.sublabelBlack};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: ${-0.45 / 18}em;
`;

const sizeGuideButtonStyle = css`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.sublabelBlack};
  margin-bottom: 8px;
`;

const scrubsId = config.get('public.productCategories.scrubs');

const OutfitItem = () => {
  const product = useProductContext();
  const { openReviewsDrawer } = useReviewsDrawer();
  const {
    bundleProducts,
    updateBundleProduct,
    product: bundleProduct,
  } = useProductDetailContext();
  const { isPlusSizeSelected, selectedPermalink, productIndex } =
    useProductDetailSelectionState();
  const selectedProduct = bundleProducts?.[productIndex];
  const { membershipLevelGroup } = useMembership();

  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const imageType = isPlusSizeSelected ? 'plus' : 'default';

  const additionalQueryVariables = {
    includeSwatchType: true,
    membershipLevelGroup,
    filterOrphanedOptions: true,
  };

  debug('bundleProduct', bundleProduct);

  if (
    bundleProduct.componentExclusions &&
    bundleProduct.componentExclusions.length
  ) {
    const excludeSkus = bundleProduct.componentExclusions.map(
      (exclusion) => exclusion.sku
    );
    additionalQueryVariables.excludeSkus = excludeSkus;
    debug('Excluding SKUs', excludeSkus);
  }

  const {
    product: loadedSelectedProduct,
    relatedProductsLoading,
    baseProductLoading,
  } = useProductQueryAndPreload({
    permalinkOrProductId: selectedPermalink,
    initialProduct: selectedProduct,
    additionalQueryVariables,
  });

  const categoryIdList = product.productCategoryIdList;
  const tagIdList = product.tagIdList;
  const isScrubs = categoryIdList?.includes(scrubsId);
  const showFitGuide = !isIneligibleFitGuideProduct(product) && !isScrubs;
  const isLoading = relatedProductsLoading || baseProductLoading;
  const useAlternateSizeGuide = tagIdList?.includes(ProductTag.altSizeGuidePDP);
  const mysteryBox = isMysteryBox(product);

  /*
    Note: we intentionally omitted isLoading in the dependencies
    because we do not want to dispatch an additional updateBundleProduct action
  */
  useEffect(() => {
    if (loadedSelectedProduct && !isLoading) {
      updateBundleProduct({
        product: loadedSelectedProduct,
        productIndex,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedSelectedProduct, productIndex, updateBundleProduct]);

  const useFableticsPDPFitGuide = config.get(
    'public.brand.useFableticsPDPFitGuide'
  );

  const handleClick = async (href) => {
    const imageUrl = getImageUrl({ product, imageType });
    const hasPromo =
      Boolean(product.promos?.promoCode) &&
      typeof product.promos?.promoCode === 'string';
    const productLabelDisplay = hasPromo
      ? formatMessage({
          id: product.promos.promoCode,
          defaultMessage: product.promos.promoCode,
        })
      : null;

    const updatedHref = href.split(/[#?]/)[0];
    const productInfo = {
      product: {
        ...product,
        promos: {
          ...product.promos,
          promoLabel: product.promos?.promoLabel || productLabelDisplay,
        },
      },
      href: updatedHref,
      fplId: product.featuredProductLocationIdList,
      image_url: imageUrl,
    };

    dispatch(
      trackProductClicked({
        ...productInfo,
        productUrl: href,
      })
    );

    window.location.href = href;
  };

  const { categoryIds: selectedProductCategoryIds = [] } = selectedProduct;

  const { showEmbroiderySelector, bundleItemProductId } =
    useProductDetailEmbroideryContext();

  return (
    <>
      <OutfitWrapper>
        <OutfitWrapperContent>
          <OutfitItemImageWrapper
            onClick={() =>
              handleClick(`/${productDetailPath}/${selectedPermalink}`)
            }
          >
            <OutfitProductImage
              breakpoints={outfitImageBreakpoints}
              disableHover
              type={imageType}
            />
            <OutfitProductName />
          </OutfitItemImageWrapper>
          <OutfitDetailsWrapper>
            <ProductDetailColorSelector
              relatedProductsLoading={isLoading}
              displayLoadingState
              disabled={isLoading}
            />
            <ProductDetailSizeSelector />
            {isScrubs && (
              <ScrubsSizeGuideModal
                categoryIdList={selectedProductCategoryIds}
                showMessaging
                showSizeCalloutMessage
                sizeGuideButtonStyle={sizeGuideButtonStyle}
                useAlternate={useAlternateSizeGuide}
              />
            )}
            {showFitGuide && !useFableticsPDPFitGuide && (
              <SizeFabricGuideModal useAlternate={useAlternateSizeGuide} />
            )}
            {showFitGuide && useFableticsPDPFitGuide && (
              <SizeFabricGuideFableticsModal
                categoryIdList={categoryIdList}
                useAlternate={useAlternateSizeGuide}
              />
            )}
            {showEmbroiderySelector &&
              product.masterProductId === bundleItemProductId && (
                <ProductDetailEmbroiderySelector />
              )}
            <ProductDetailOtherSelectors />
          </OutfitDetailsWrapper>
        </OutfitWrapperContent>
        <ProductDetailsSection product={selectedProduct} showBundleStyles />
        {!!product?.reviewCount && !mysteryBox && (
          <OutfitWrapperActions onClick={() => openReviewsDrawer(product)}>
            <ReviewsHeader />
            <BsArrowRight />
          </OutfitWrapperActions>
        )}
      </OutfitWrapper>
    </>
  );
};

export default OutfitItem;
